import { Link } from 'react-router-dom';

const EFFECTIVE_DATE = '14 May 2025';

export function Terms() {
  return (
    <div className="min-h-screen bg-bg-base text-white">
      <nav className="border-b border-white/[0.06] px-6 py-4 flex items-center justify-between max-w-4xl mx-auto">
        <Link to="/" className="flex items-center gap-2">
          <div className="w-7 h-7 bg-primary-500 rounded-lg flex items-center justify-center font-bold text-white text-sm">Cr</div>
          <span className="font-semibold tracking-tight">Cros<span className="text-secondary-400">ova</span></span>
        </Link>
        <Link to="/login" className="text-[13px] text-white/50 hover:text-white transition-colors">Log in</Link>
      </nav>

      <main className="max-w-4xl mx-auto px-6 py-14">
        <h1 className="text-3xl font-bold mb-2">Terms of Service</h1>
        <p className="text-white/40 text-sm mb-10">Effective date: {EFFECTIVE_DATE}</p>

        <div className="prose prose-invert prose-sm max-w-none space-y-8 text-white/70 leading-relaxed text-[13.5px]">

          <section>
            <h2 className="text-base font-semibold text-white mb-2">1. About Crosova</h2>
            <p>
              Crosova ("we", "us", "our") is a cross-listing platform that helps sellers list items across multiple
              second-hand marketplaces from one place. Our service is available at{' '}
              <a href="https://crosova.com" className="text-primary-300 hover:underline">crosova.com</a> and via our
              API. By creating an account or using our service, you agree to these Terms of Service.
            </p>
            <p className="mt-2">
              Contact: <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">2. Eligibility</h2>
            <p>
              You must be at least 18 years old and legally able to enter into contracts in your jurisdiction to use
              Crosova. By creating an account, you confirm that you meet these requirements.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">3. Your Account</h2>
            <p>
              You are responsible for maintaining the confidentiality of your account credentials and for all activity
              that occurs under your account. Notify us immediately at{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>{' '}
              if you believe your account has been compromised.
            </p>
            <p className="mt-2">
              You may not share your account with others or create multiple accounts to circumvent plan limits.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">4. Free Trial</h2>
            <p>
              New accounts receive a 14-day free trial with access to all Pro features. No credit card is required to
              start the trial. At the end of the trial period, access to paid features will be suspended until you
              subscribe to a paid plan. We reserve the right to modify or end the free trial offer at any time.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">5. Subscriptions and Billing</h2>
            <p>
              Paid plans are billed monthly in advance. Prices are in GBP and include VAT where applicable. Payment is
              processed securely by Stripe. We do not store your card details.
            </p>
            <p className="mt-2">
              You may cancel your subscription at any time from your account settings. Cancellation takes effect at the
              end of the current billing period — you retain access until that date. We do not provide refunds for
              partial periods.
            </p>
            <p className="mt-2">
              We may change our pricing with 30 days' notice. Continued use after the effective date constitutes
              acceptance of the new pricing.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">6. Acceptable Use</h2>
            <p>You agree not to use Crosova to:</p>
            <ul className="list-disc pl-5 mt-2 space-y-1">
              <li>List counterfeit, stolen, or prohibited goods</li>
              <li>Violate the terms of service of any connected marketplace</li>
              <li>Attempt to reverse-engineer, scrape, or otherwise abuse the platform</li>
              <li>Transmit malware, spam, or any harmful content</li>
              <li>Impersonate another person or entity</li>
              <li>Use the service in any way that breaches applicable law</li>
            </ul>
            <p className="mt-2">
              We reserve the right to suspend or terminate accounts that violate these terms without notice.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">7. Third-Party Marketplaces</h2>
            <p>
              Crosova connects to third-party marketplaces (eBay, Vinted, Etsy, etc.) on your behalf using credentials
              you provide. You remain solely responsible for complying with the terms of service of each marketplace.
              Crosova is not affiliated with, endorsed by, or responsible for any third-party marketplace.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">8. Intellectual Property</h2>
            <p>
              You retain ownership of all content you upload (listing text, photos, etc.). By using the service, you
              grant Crosova a limited licence to store, display, and transmit your content solely for the purpose of
              providing the service.
            </p>
            <p className="mt-2">
              The Crosova brand, software, and all platform content are owned by us and protected by copyright and
              other intellectual property laws. You may not copy, modify, or distribute any part of the platform
              without our written permission.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">9. Disclaimers</h2>
            <p>
              Crosova is provided "as is" without warranties of any kind, express or implied. We do not guarantee
              uninterrupted or error-free operation. AI-generated content (titles, descriptions, price estimates) is
              provided for guidance only — you are responsible for reviewing it before publishing.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">10. Limitation of Liability</h2>
            <p>
              To the fullest extent permitted by law, Crosova shall not be liable for any indirect, incidental,
              special, or consequential damages arising from your use of the service, including loss of profits, data,
              or listings. Our total liability to you shall not exceed the amount you paid us in the 12 months
              preceding the claim.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">11. Termination</h2>
            <p>
              You may close your account at any time by contacting us at{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>.
              We may suspend or terminate your account if you breach these terms. Upon termination, your data will be
              retained for 30 days then permanently deleted, in accordance with our Privacy Policy.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">12. Changes to These Terms</h2>
            <p>
              We may update these terms from time to time. We will notify you by email or in-app notice at least 14
              days before material changes take effect. Continued use after the effective date constitutes acceptance.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">13. Governing Law</h2>
            <p>
              These terms are governed by the laws of England and Wales. Any disputes shall be subject to the exclusive
              jurisdiction of the courts of England and Wales.
            </p>
          </section>

          <section>
            <h2 className="text-base font-semibold text-white mb-2">14. Contact</h2>
            <p>
              Questions about these terms? Email us at{' '}
              <a href="mailto:admin@crosova.com" className="text-primary-300 hover:underline">admin@crosova.com</a>.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-white/[0.06] mt-16 py-8 text-center text-[11.5px] text-white/30">
        <div className="flex items-center justify-center gap-4 mb-2">
          <Link to="/terms" className="text-white/50">Terms</Link>
          <Link to="/privacy" className="hover:text-white/60 transition-colors">Privacy</Link>
          <a href="mailto:admin@crosova.com" className="hover:text-white/60 transition-colors">Contact</a>
        </div>
        <div>© {new Date().getFullYear()} Crosova. All rights reserved.</div>
      </footer>
    </div>
  );
}
